import React, { useContext, useMemo, useState } from 'react'
import { ModalProps } from 'components/styled/modal/modal-component'

export const AlertVariants = {
  SUCCESS: 'success',
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error',
} as const

export type AlertVariant = typeof AlertVariants[keyof typeof AlertVariants]

export interface AlertProps
  extends Pick<
    ModalProps,
    'id' | 'showCloseButton' | 'size' | 'closeLabel'
  > {
  variant: AlertVariant
  title: string | React.ReactNode
  text?: string | React.ReactNode
  closeAfterTimeout?: boolean
  closeTimeoutMs?: number
  children?: React.ReactNode
}

export interface AlertContextType {
  openAlerts: AlertProps[]
  onOpenAlert: (props: AlertProps) => void
  onCloseAlert: (id: string) => void
  closeAllAlerts: () => void
}

export const AlertContext = React.createContext<AlertContextType>({
  openAlerts: [],
  onOpenAlert: _props => {},
  onCloseAlert: _id => {},
  closeAllAlerts: () => {},
})

interface AlertContextProviderProps {
  children?: React.ReactNode
}

export const AlertContextProvider: React.FC<AlertContextProviderProps> = ({ children }) => {
  const [openAlerts, setOpenAlerts] = useState<AlertProps[]>([])

  const onOpenAlert = (props: AlertProps) => {
    setOpenAlerts(oldVal => [...oldVal, props])
  }

  const onCloseAlert = (id: string) => {
    const index = openAlerts.findIndex(alert => alert.id === id)
    if (index > -1) {
      setOpenAlerts(oldAlerts => [
        ...oldAlerts.slice(0, index),
        ...oldAlerts.slice(index + 1),
      ])
    }
  }

  const closeAllAlerts = () => {
    setOpenAlerts([])
  }

  const contextValue = useMemo(
    () => ({
      openAlerts,
      onOpenAlert,
      onCloseAlert,
      closeAllAlerts,
    }),
    [openAlerts]
  )

  return (
    <AlertContext.Provider value={contextValue}>
      {children}
    </AlertContext.Provider>
  )
}

export const useAlertContext = () => useContext(AlertContext)
